import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Selector, Toast } from 'sonata-react-components';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import {
  countriesOptions,
  getCapitalTimeZone,
  getTimeZonesByCountry
} from '@/helpers/countriesOptions/countriesOptions.helper';
import './settingsSection.styles.scss';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { MultiFlightEntityType } from 'sonata-static-storage/lib/common/common.types';

type Props = {
  countries?: string[];
  defaultCountries: SelectorOptionPropsInterface[];
  entity: MultiFlightEntityType;
  errors: Record<string, any>;
  ioNumber?: string;
  name?: string;
  onCountriesChange: (options: SelectorOptionPropsInterface[]) => void;
  onIoNameChange: (ioNameData: string) => void;
  onIoNumberChange?: (ioNumberData: string) => void;
  timezone?: string;
  onTimeZoneChange?: (options: SelectorOptionPropsInterface) => void;
};

type State = {
  showErrorToast: boolean;
};

export function SettingsSection({
  countries,
  defaultCountries,
  entity,
  errors,
  ioNumber,
  name,
  onCountriesChange,
  onIoNameChange,
  onIoNumberChange,
  timezone,
  onTimeZoneChange
}: Props) {
  const { formatMessage } = useIntl();
  const [{ showErrorToast }, setState] = useState<State>({ showErrorToast: true });

  const hasSomeError = ['name', 'ioNumber', 'countries']
    // biome-ignore lint/suspicious/noPrototypeBuiltins: off
    .some(field => Object.prototype.hasOwnProperty.call(errors, field));

  const handleToastClose = () => setState(prevState => ({ ...prevState, showErrorToast: false }));

  const showIONumber = entity === 'insertionOrder';
  const isLineItem = entity === 'lineItem';

  const selectedCountries = worldCountries
    .filter(country => countries?.includes(country.cca3.toLocaleLowerCase()))
    .map(country => ({
      id: country.cca3.toLocaleLowerCase(),
      label: country.name.common
    }));

  const timeZones = getTimeZonesByCountry(selectedCountries[0]?.id || '').map(tz => ({ id: tz, label: tz }));

  const selectedTimeZone = timeZones.filter(tz => tz.id === timezone);

  return (
    <div className="settings">
      <div className="settings__settingsBox" id={'settingsSection-box'}>
        <header className="settings__title">
          {formatMessage({
            defaultMessage: `${entity === 'insertionOrder' ? 'Insertion Order' : 'Line Item'} Settings`,
            id: `ioSettings.insertionOrderSettinggsTitle.${entity}`
          })}
        </header>
        <div>
          {hasSomeError && showErrorToast && (
            <Toast
              autoClose={false}
              className="settings__errorToast"
              closeToast={handleToastClose}
              icon="fal fa-exclamation-triangle"
              text={formatMessage({
                defaultMessage: 'Please fix the errors below to proceed',
                id: 'ioSettings.fixErrorsMessage'
              })}
              type="danger"
            />
          )}
          <div className="settings__selectorsContainer">
            <Selector
              className="settings__countrySelector"
              defaultValue={defaultCountries}
              description={errors?.countries?.message}
              isDisabled={isLineItem}
              label={formatMessage({
                defaultMessage: 'Country',
                id: 'ioSettings.countrySelector'
              })}
              onChange={(options: SelectorOptionPropsInterface[]) => onCountriesChange(options)}
              options={countriesOptions}
              selectorStatus={errors?.countries ? 'wrong' : 'default'}
              type="multiple"
              value={selectedCountries}
            />
            {isLineItem && (
              <div className="settings__timeZoneSelectorContainer">
                <Selector
                  className="settings__timeZoneSelector"
                  defaultValue={timeZones.filter(tz => tz.id === getCapitalTimeZone(selectedCountries[0]?.id))}
                  description={errors?.timezone?.message}
                  isDisabled={timeZones.length <= 1}
                  label={formatMessage({
                    defaultMessage: 'Time Zone',
                    id: 'ioSettings.timeZoneSelector'
                  })}
                  onChange={(options: SelectorOptionPropsInterface) => onTimeZoneChange?.(options)}
                  options={timeZones}
                  selectorStatus={errors?.timeZone ? 'wrong' : 'default'}
                  type="simple"
                  value={selectedTimeZone}
                />
              </div>
            )}
            {showIONumber && (
              <div className="settings__ioNumberInputContainer">
                <Input
                  className="settings__ioNumberInput"
                  description={errors?.ioNumber?.message}
                  id={'settings-ioNumber'}
                  inputStatus={errors?.ioNumber ? 'wrong' : 'default'}
                  label={formatMessage({
                    defaultMessage: 'IO number',
                    id: 'ioSettings.inputIoNumber'
                  })}
                  name="ioNumber"
                  onChange={(value: string | FileList | null) => onIoNumberChange?.(value as string)}
                  shape="squareUI"
                  type="text"
                  value={ioNumber}
                />
              </div>
            )}
            <div className="settings__ioNameInputContainer">
              <Input
                className="settings__ioNameInput"
                description={errors?.name?.message}
                id={'settings-ioName'}
                inputStatus={errors?.name ? 'wrong' : 'default'}
                label={formatMessage({
                  defaultMessage: 'Name',
                  id: 'ioSettings.inputName'
                })}
                name="ioName"
                onChange={(entityName: string | FileList | null) => onIoNameChange(entityName as string)}
                shape="squareUI"
                type="text"
                value={name}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
